import React, { useState, useEffect, useMemo } from 'react';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import OfFinanceiroDashboard from './OfFinanceiroDashboard';
import * as XLSX from 'xlsx';
import { 
  DollarSign, TrendingUp, TrendingDown, Clock, Package, 
  Settings, ChevronRight, ChevronDown, ChevronLeft, Eye, Download,
  FileSpreadsheet, FileText, AlertTriangle, CheckCircle2,
  Wrench, XCircle, Percent, Filter, User, Edit, Calendar, Search,
  Square, CheckSquare, ArrowUpDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helpers de formatação partilhados
import { escapeHtml } from '../../utils/format';

// Componente: input editável de custo unitário (€/peça) por OF para Arranjo / Defeito
const CustoUnitarioInput = ({ of, tipo, cor, onUpdated }) => {
  const isArranjo = tipo === 'arranjo';
  const qtd = isArranjo ? (of.arranjos || 0) : (of.defeitos || 0);
  const valorEfetivo = isArranjo ? of.custo_por_arranjo_unitario : of.custo_por_defeito_unitario;
  const valorDefault = isArranjo ? of.custo_por_arranjo_default : of.custo_por_defeito_default;
  const totalCusto = isArranjo ? of.custo_arranjos : of.custo_defeitos;
  const isOverride = valorEfetivo !== valorDefault && valorEfetivo != null;
  const [valor, setValor] = useState(valorEfetivo != null ? String(valorEfetivo) : '');
  const [saving, setSaving] = useState(false);
  // Re-sincroniza quando a prop mudar (após reload)
  useEffect(() => {
    setValor(valorEfetivo != null ? String(valorEfetivo) : '');
  }, [valorEfetivo]);
  const handleSave = async () => {
    const v = parseFloat(valor);
    if (isNaN(v) || v < 0) {
      toast.error('Valor inválido');
      return;
    }
    setSaving(true);
    try {
      const body = isArranjo ? { custo_por_arranjo: v } : { custo_por_defeito: v };
      await axios.patch(`${API}/ofs/${of.of_id}/custos-anomalias`, body);
      toast.success(`Custo/peça ${isArranjo ? 'Arranjo' : 'Defeito'} atualizado`);
      if (onUpdated) await onUpdated();
    } catch (err) {
      toast.error('Erro a guardar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };
  const palette = cor === 'blue' 
    ? { border: 'border-blue-300', bg: 'bg-blue-50', text: 'text-blue-700', label: 'text-blue-600', focus: 'focus-visible:ring-blue-400' }
    : { border: 'border-red-300', bg: 'bg-red-50', text: 'text-red-700', label: 'text-red-600', focus: 'focus-visible:ring-red-400' };
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${palette.bg} border ${palette.border}`} data-testid={`custo-unit-${tipo}-${of.of_id}`}>
      <div className="flex flex-col">
        <span className={`text-[10px] uppercase font-bold ${palette.label}`}>{isArranjo ? 'Arranjos' : 'Defeitos'}</span>
        <span className={`text-xs ${palette.text} font-medium`}>{qtd} pç{qtd !== 1 ? 's' : ''}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs text-stone-500">×</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={handleSave}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          disabled={saving}
          className={`w-20 h-8 px-2 text-sm font-semibold rounded border ${palette.border} bg-white ${palette.text} focus-visible:outline-none focus-visible:ring-2 ${palette.focus}`}
          data-testid={`custo-unit-input-${tipo}-${of.of_id}`}
          title={isOverride ? `Personalizado para esta OF (default global: ${valorDefault} €)` : 'Default global — clique para personalizar nesta OF'}
        />
        <span className="text-xs text-stone-500">€/pç</span>
      </div>
      <span className={`text-xs ${palette.text} font-bold ml-1`}>= -{(totalCusto || 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}</span>
      {isOverride && (
        <span className="text-[9px] uppercase font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title={`Default global: ${valorDefault} €`}>OF</span>
      )}
    </div>
  );
};

export default function Financeiro() {
  const [loading, setLoading] = useState(true);
  const [analise, setAnalise] = useState(null);
  const [config, setConfig] = useState({
    taxa_hora_operador: 10,
    custo_energia_dia: 0,
    custo_agua_dia: 0,
    custo_consumiveis_dia: 0,
    percentagem_overhead: 10,
    margem_lucro: 20,
    custo_por_arranjo: 1.5,
    custo_por_defeito: 3.0
  });
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [expandedOfs, setExpandedOfs] = useState({});
  // Dialog "Dashboard Financeiro" da OF
  const [showOfDashboard, setShowOfDashboard] = useState(false);
  const [ofDashboard, setOfDashboard] = useState(null); // OF actualmente exibida no dashboard
  const [dashboardFocoCustos, setDashboardFocoCustos] = useState(false); // se entrou via "$"
  const [filtroStatus, setFiltroStatus] = useState('todos'); // todos, em_curso, terminado
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [filtroMarca, setFiltroMarca] = useState('todas');
  const [ordenacao, setOrdenacao] = useState('recente'); // recente | antigo | concluida | em_curso
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOfs, setSelectedOfs] = useState({});
  const [qtdFinalMap, setQtdFinalMap] = useState({});
  
  // Período
  const [periodoAtivo, setPeriodoAtivo] = useState('tudo'); // tudo, dia, semana, mes, intervalo
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  
  // Modal de custos adicionais
  const [showCustosModal, setShowCustosModal] = useState(false);
  const [selectedOfParaCusto, setSelectedOfParaCusto] = useState(null);
  const [novoCusto, setNovoCusto] = useState({ descricao: '', valor: '', tipo: 'outros' });
  const [editingCusto, setEditingCusto] = useState(null); // Custo em edição

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (periodoAtivo !== 'intervalo') {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodoAtivo]);

  // Reset da página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, filtroStatus, pageSize]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Calcular datas com base no período
      let params = {};
      const hoje = new Date();
      if (periodoAtivo === 'dia') {
        const diaStr = hoje.toISOString().split('T')[0];
        params = { data_inicio: diaStr, data_fim: diaStr };
      } else if (periodoAtivo === 'semana') {
        const seg = new Date(hoje);
        seg.setDate(hoje.getDate() - hoje.getDay() + (hoje.getDay() === 0 ? -6 : 1));
        params = { data_inicio: seg.toISOString().split('T')[0], data_fim: hoje.toISOString().split('T')[0] };
      } else if (periodoAtivo === 'mes') {
        const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        params = { data_inicio: primeiroDia.toISOString().split('T')[0], data_fim: hoje.toISOString().split('T')[0] };
      } else if (periodoAtivo === 'intervalo' && dataInicio && dataFim) {
        params = { data_inicio: dataInicio, data_fim: dataFim };
      }
      // periodoAtivo === 'tudo' → sem filtro de datas
      
      const [analiseRes, configRes] = await Promise.all([
        axios.get(`${API}/financeiro/analise-completa`, { params }),
        axios.get(`${API}/configuracao-financeira`)
      ]);
      setAnalise(analiseRes.data);
      setConfig(configRes.data);
      // Inicializar qtdFinalMap com valores salvos ou quantidade_total
      const map = {};
      (analiseRes.data?.ofs || []).forEach(of => {
        map[of.of_id] = of.quantidade_final != null ? of.quantidade_final : of.quantidade_total;
      });
      setQtdFinalMap(prev => {
        const merged = { ...map };
        // Manter edições locais que o utilizador já fez
        Object.keys(prev).forEach(k => { if (prev[k] !== undefined && map[k] !== undefined) merged[k] = prev[k]; });
        return merged;
      });
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/configuracao-financeira`, config);
      toast.success('Configurações guardadas!');
      setShowConfigDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  // Guardar quantidade final editada
  const saveQtdFinal = async (ofId, qtd) => {
    try {
      await axios.patch(`${API}/financeiro/of/${ofId}/quantidade-final`, { quantidade_final: qtd });
      toast.success('Quantidade final guardada');
    } catch (error) {
      toast.error('Erro ao guardar quantidade');
    }
  };

  // Obter qtdFinal para uma OF (para cálculos)
  const getQtdFinal = (of) => {
    const val = qtdFinalMap[of.of_id];
    return val != null && val > 0 ? val : null;
  };

  // Funções de custos adicionais
  const handleAddCusto = async () => {
    if (!novoCusto.descricao || !novoCusto.valor) {
      toast.error('Preencha a descrição e o valor');
      return;
    }
    
    try {
      await axios.post(`${API}/ofs/${selectedOfParaCusto.of_id}/custos-adicionais`, {
        ...novoCusto,
        valor: parseFloat(novoCusto.valor)
      });
      toast.success('Custo adicionado!');
      setNovoCusto({ descricao: '', valor: '', tipo: 'outros' });
      setShowCustosModal(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao adicionar custo');
    }
  };

  const handleRemoveCusto = async (custoId) => {
    try {
      await axios.delete(`${API}/custos-adicionais/${custoId}`);
      toast.success('Custo removido!');
      loadData();
    } catch (error) {
      toast.error('Erro ao remover custo');
    }
  };

  const handleEditCusto = (custo) => {
    setEditingCusto(custo);
    setNovoCusto({
      descricao: custo.descricao,
      valor: custo.valor.toString(),
      tipo: custo.tipo || 'outros'
    });
  };

  const handleUpdateCusto = async () => {
    if (!novoCusto.descricao || !novoCusto.valor) {
      toast.error('Preencha a descrição e o valor');
      return;
    }
    
    try {
      await axios.put(`${API}/custos-adicionais/${editingCusto.id}`, {
        ...novoCusto,
        valor: parseFloat(novoCusto.valor)
      });
      toast.success('Custo actualizado!');
      setEditingCusto(null);
      setNovoCusto({ descricao: '', valor: '', tipo: 'outros' });
      loadData();
    } catch (error) {
      toast.error('Erro ao actualizar custo');
    }
  };

  const cancelEditCusto = () => {
    setEditingCusto(null);
    setNovoCusto({ descricao: '', valor: '', tipo: 'outros' });
  };

  const toggleOfExpanded = (ofId) => {
    setExpandedOfs(prev => ({
      ...prev,
      [ofId]: !prev[ofId]
    }));
  };

  // Abrir Dashboard Financeiro da OF (modal)
  const openOfDashboard = (of, focoCustos = false) => {
    setOfDashboard(of);
    setDashboardFocoCustos(focoCustos);
    setShowOfDashboard(true);
  };
  // Sincroniza ofDashboard com a versão actualizada após reload
  useEffect(() => {
    if (showOfDashboard && ofDashboard && analise?.ofs) {
      const fresh = analise.ofs.find(o => o.of_id === ofDashboard.of_id);
      if (fresh && fresh !== ofDashboard) setOfDashboard(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analise]);

  const formatTempo = (segundos) => {
    if (!segundos || segundos === 0) return '-';
    const abs = Math.abs(segundos);
    const sinal = segundos < 0 ? '-' : '';
    if (abs < 60) {
      return `${sinal}${Math.round(abs)}s`;
    }
    const horas = Math.floor(abs / 3600);
    const minutos = Math.floor((abs % 3600) / 60);
    if (horas === 0) {
      return `${sinal}${minutos}min`;
    }
    return `${sinal}${horas}h ${minutos}min`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const getEficienciaColor = (eficiencia) => {
    if (eficiencia >= 100) return 'text-emerald-600';
    if (eficiencia >= 80) return 'text-amber-600';
    return 'text-red-600';
  };

  const getEficienciaBg = (eficiencia) => {
    if (eficiencia >= 100) return 'bg-emerald-50';
    if (eficiencia >= 80) return 'bg-amber-50';
    return 'bg-red-50';
  };

  const getDesvioIcon = (status) => {
    if (status === 'mais_rapido') return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    if (status === 'mais_lento') return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <CheckCircle2 className="h-4 w-4 text-stone-400" />;
  };

  // Helper: OFs a exportar (selecionadas ou todas filtradas)
  const getOfsParaExportar = () => {
    const ids = Object.keys(selectedOfs).filter(id => selectedOfs[id]);
    if (ids.length > 0) {
      return ofsFiltradas.filter(of => ids.includes(of.of_id));
    }
    return ofsFiltradas;
  };

  // Exportar para CSV
  const exportCSV = () => {
    const ofs = getOfsParaExportar();
    if (ofs.length === 0) { toast.error('Nenhuma OF para exportar'); return; }
    
    let csv = 'OF;Cliente;Artigo;Status;Qtd Total;Qtd Produzida;Arranjos;Defeitos;Tempo Previsto (h);Tempo Real (h);Eficiência %;Custo Estimado;Custo Real;Custo/Peça;Variação\n';
    
    ofs.forEach(of => {
      const custoEstimado = (of.tempo_previsto_segundos / 3600) * config.taxa_hora_operador;
      const qtdF = qtdFinalMap[of.of_id] || of.quantidade_total;
      const custoPeca = qtdF > 0 ? (of.custo_total / qtdF).toFixed(2) : '-';
      csv += `${of.numero_of};${of.cliente || ''};${of.artigo || ''};${of.status};${of.quantidade_total};${qtdF};${of.arranjos};${of.defeitos};${(of.tempo_previsto_segundos/3600).toFixed(2)};${(of.tempo_real_segundos/3600).toFixed(2)};${of.eficiencia_percentual};${custoEstimado.toFixed(2)};${of.custo_total};${custoPeca};${of.variacao_custo}\n`;
    });
    
    csv += '\n\nDETALHE POR OPERAÇÃO\n';
    csv += 'OF;Operação;Código;Tempo Previsto (h);Tempo Real (h);Desvio %;Eficiência %;Custo Estimado;Custo Real;Peças;Status\n';
    
    ofs.forEach(of => {
      of.operacoes.forEach(op => {
        const custoEstOp = (op.tempo_previsto_segundos / 3600) * config.taxa_hora_operador;
        csv += `${of.numero_of};${op.operacao_nome};${op.operacao_codigo};${(op.tempo_previsto_segundos/3600).toFixed(2)};${(op.tempo_real_segundos/3600).toFixed(2)};${op.desvio_percentual};${op.eficiencia_percentual};${custoEstOp.toFixed(2)};${op.custo};${op.pecas_produzidas};${op.status}\n`;
      });
    });
    
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `analise_financeira_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exportado!');
  };

  // Exportar para Excel (.xlsx real)
  const exportExcel = () => {
    const ofs = getOfsParaExportar();
    if (ofs.length === 0) { toast.error('Nenhuma OF para exportar'); return; }
    
    const resumoData = ofs.map(of => {
      const custoEst = ((of.tempo_previsto_segundos || 0) / 3600) * config.taxa_hora_operador;
      const qtdF = qtdFinalMap[of.of_id] || of.quantidade_total;
      const custoPeca = qtdF > 0 ? Number((of.custo_total / qtdF).toFixed(2)) : 0;
      return {
        'OF': of.numero_of,
        'Cliente': of.cliente || '',
        'Artigo': of.artigo || '',
        'Status': of.status,
        'Qtd Lote': of.quantidade_total,
        'Qtd Final': qtdF,
        'Arranjos': of.arranjos,
        'Defeitos': of.defeitos,
        'Tempo Prev. (h)': Number((of.tempo_previsto_segundos / 3600).toFixed(2)),
        'Tempo Real (h)': Number((of.tempo_real_segundos / 3600).toFixed(2)),
        'Eficiência %': of.eficiencia_percentual,
        'Custo Estimado': Number(custoEst.toFixed(2)),
        'Custo Real': of.custo_total,
        'Custo/Peça': custoPeca,
        'Variação': of.variacao_custo
      };
    });
    
    const detalheData = [];
    ofs.forEach(of => {
      of.operacoes.forEach(op => {
        const custoEstOp = ((op.tempo_previsto_segundos || 0) / 3600) * config.taxa_hora_operador;
        detalheData.push({
          'OF': of.numero_of,
          'Operação': op.operacao_nome,
          'Código': op.operacao_codigo || '',
          'Tempo Prev. (h)': Number((op.tempo_previsto_segundos / 3600).toFixed(2)),
          'Tempo Real (h)': Number((op.tempo_real_segundos / 3600).toFixed(2)),
          'Desvio %': op.desvio_percentual,
          'Eficiência %': op.eficiencia_percentual,
          'Custo Estimado': Number(custoEstOp.toFixed(2)),
          'Custo Real': op.custo,
          'Peças': op.pecas_produzidas,
          'Status': op.status
        });
      });
    });
    
    const wb = XLSX.utils.book_new();
    const wsResumo = XLSX.utils.json_to_sheet(resumoData);
    const wsDetalhe = XLSX.utils.json_to_sheet(detalheData);
    XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo OFs');
    XLSX.utils.book_append_sheet(wb, wsDetalhe, 'Detalhe Operações');
    XLSX.writeFile(wb, `analise_financeira_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel (.xlsx) exportado!');
  };

  // Exportar PDF de uma OF específica via jsPDF + autoTable
  const exportPDFOf = (of) => {
    if (!of) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const custoEstOf = ((of.tempo_previsto_segundos || 0) / 3600) * config.taxa_hora_operador;
      const qtdF = qtdFinalMap[of.of_id] || of.quantidade_total;
      const custoPeca = qtdF > 0 ? formatCurrency(of.custo_total / qtdF) : '-';
      
      doc.setFontSize(16);
      doc.text(`Analise Financeira - OF ${of.numero_of}`, 14, 15);
      doc.setFontSize(9);
      const infoItems = [
        of.marca ? `Marca: ${of.marca}` : null,
        of.artigo ? `Artigo: ${of.artigo}` : null,
        of.cliente ? `Cliente: ${of.cliente}` : null,
        of.data_entrega ? `Entrega: ${of.data_entrega}` : null,
        `Data: ${new Date().toLocaleDateString('pt-PT')}`,
        `Status: ${of.status}`
      ].filter(Boolean).join('  |  ');
      doc.text(infoItems, 14, 22);
      
      // Resumo KPIs
      autoTable(doc, {
        startY: 27,
        head: [['Qtd Lote', 'Qtd Final', 'Arr./Def.', 'Tempo Prev.', 'Tempo Real', 'Eficiencia', 'Custo Estimado', 'Custo Real', 'Custo/Peca']],
        body: [[
          of.quantidade_total,
          qtdF,
          `${of.arranjos}/${of.defeitos}`,
          formatTempo(of.tempo_previsto_segundos),
          formatTempo(of.tempo_real_segundos),
          `${of.eficiencia_percentual}%`,
          formatCurrency(custoEstOf),
          formatCurrency(of.custo_total),
          custoPeca
        ]],
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [80, 80, 80], textColor: 255 }
      });
      
      // Tabela de operações
      const opsBody = of.operacoes.map(op => {
        const custoEstOp = ((op.tempo_previsto_segundos || 0) / 3600) * config.taxa_hora_operador;
        const operadores = op.operadores?.map(o => `${o.nome} (${o.eficiencia}%)`).join(', ') || '-';
        return [
          op.operacao_nome + (op.operacao_codigo ? ` [${op.operacao_codigo}]` : ''),
          operadores,
          formatTempo(op.tempo_previsto_segundos),
          formatTempo(op.tempo_real_segundos),
          op.tempo_real_segundos > 0 ? `${op.desvio_percentual > 0 ? '+' : ''}${op.desvio_percentual}%` : '-',
          op.tempo_real_segundos > 0 ? `${op.eficiencia_percentual}%` : '-',
          formatCurrency(custoEstOp),
          formatCurrency(op.custo),
          op.pecas_produzidas
        ];
      });
      
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Operacao', 'Operador(es)', 'Tempo Prev.', 'Tempo Real', 'Desvio', 'Eficiencia', 'Custo Est.', 'Custo Real', 'Pecas']],
        body: opsBody,
        theme: 'striped',
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [100, 100, 100], textColor: 255 },
        columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 50 } }
      });
      
      doc.save(`financeiro_OF_${of.numero_of}.pdf`);
      toast.success('PDF exportado com sucesso');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  // Exportar PDF geral via jsPDF + autoTable
  const exportPDF = () => {
    const ofs = getOfsParaExportar();
    if (ofs.length === 0) { toast.error('Nenhuma OF para exportar'); return; }
    
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      
      doc.setFontSize(16);
      doc.text('Analise Financeira de Producao', 14, 15);
      doc.setFontSize(9);
      doc.text(`Data: ${new Date().toLocaleDateString('pt-PT')} | OFs: ${ofs.length} | Taxa/Hora: ${formatCurrency(config.taxa_hora_operador)}`, 14, 22);
      
      // Tabela resumo
      const resumoBody = ofs.map(of => {
        const custoEst = ((of.tempo_previsto_segundos || 0) / 3600) * config.taxa_hora_operador;
        const qtdF = qtdFinalMap[of.of_id] || of.quantidade_total;
        const custoPeca = qtdF > 0 ? formatCurrency(of.custo_total / qtdF) : '-';
        return [
          of.numero_of,
          of.marca || '-',
          of.artigo || '-',
          of.cliente || '-',
          of.data_entrega || '-',
          of.status,
          `${qtdF}`,
          formatTempo(of.tempo_previsto_segundos),
          formatTempo(of.tempo_real_segundos),
          of.tempo_real_segundos > 0 ? `${of.eficiencia_percentual}%` : '-',
          formatCurrency(custoEst),
          formatCurrency(of.custo_total),
          custoPeca
        ];
      });
      
      autoTable(doc, {
        startY: 27,
        head: [['OF', 'Marca', 'Artigo', 'Cliente', 'Entrega', 'Status', 'Qtd', 'T.Prev.', 'T.Real', 'Efic.', 'C.Est.', 'C.Real', 'C./Peca']],
        body: resumoBody,
        theme: 'grid',
        styles: { fontSize: 6.5, cellPadding: 2 },
        headStyles: { fillColor: [80, 80, 80], textColor: 255 }
      });
      
      // Detalhe por OF
      ofs.forEach(of => {
        const opsBody = of.operacoes.map(op => {
          const custoEstOp = ((op.tempo_previsto_segundos || 0) / 3600) * config.taxa_hora_operador;
          return [
            op.operacao_nome,
            formatTempo(op.tempo_previsto_segundos),
            formatTempo(op.tempo_real_segundos),
            op.tempo_real_segundos > 0 ? `${op.desvio_percentual > 0 ? '+' : ''}${op.desvio_percentual}%` : '-',
            op.tempo_real_segundos > 0 ? `${op.eficiencia_percentual}%` : '-',
            formatCurrency(custoEstOp),
            formatCurrency(op.custo),
            op.pecas_produzidas
          ];
        });
        
        if (opsBody.length > 0) {
          let startY = doc.lastAutoTable.finalY + 8;
          if (startY > 160) { doc.addPage(); startY = 15; }
          
          // Cabeçalho da OF com detalhes
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text(`OF ${of.numero_of}`, 14, startY);
          doc.setFont(undefined, 'normal');
          doc.setFontSize(8);
          const infoLine = [
            of.marca ? `Marca: ${of.marca}` : null,
            of.artigo ? `Artigo: ${of.artigo}` : null,
            of.data_entrega ? `Entrega: ${of.data_entrega}` : null,
            `Status: ${of.status}`
          ].filter(Boolean).join('  |  ');
          doc.text(infoLine, 14, startY + 4);
          
          // Resumo KPIs da OF
          const custoEstOf = ((of.tempo_previsto_segundos || 0) / 3600) * config.taxa_hora_operador;
          const qtdF = qtdFinalMap[of.of_id] || of.quantidade_total;
          const custoPecaOf = qtdF > 0 ? formatCurrency(of.custo_total / qtdF) : '-';
          
          autoTable(doc, {
            startY: startY + 7,
            head: [['Qtd Lote', 'Qtd Final', 'Arr./Def.', 'Tempo Prev.', 'Tempo Real', 'Efic.', 'Custo Est.', 'Custo Real', 'Custo/Peca']],
            body: [[
              of.quantidade_total,
              qtdF,
              `${of.arranjos}/${of.defeitos}`,
              formatTempo(of.tempo_previsto_segundos),
              formatTempo(of.tempo_real_segundos),
              of.tempo_real_segundos > 0 ? `${of.eficiencia_percentual}%` : '-',
              formatCurrency(custoEstOf),
              formatCurrency(of.custo_total),
              custoPecaOf
            ]],
            theme: 'grid',
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [60, 60, 60], textColor: 255 }
          });
          
          // Tabela de operações
          autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 2,
            head: [['Operacao', 'Tempo Prev.', 'Tempo Real', 'Desvio', 'Efic.', 'Custo Est.', 'Custo Real', 'Pecas']],
            body: opsBody,
            theme: 'striped',
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [100, 100, 100], textColor: 255 }
          });
        }
      });
      
      doc.save(`analise_financeira_${new Date().toISOString().slice(0,10)}.pdf`);
      toast.success('PDF exportado com sucesso');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  // Helper: normaliza nome de marca (trim + collapse espaços + Title Case-ish para casar variantes)
  const normMarca = (m) => {
    if (!m) return '';
    return String(m).trim().replace(/\s+/g, ' ').toLowerCase();
  };

  // Filtrar OFs por status, marca e pesquisa de texto (memoizado para evitar trabalho redundante)
  const ofsFiltradas = useMemo(() => {
    const filtroMarcaNorm = normMarca(filtroMarca === 'todas' ? '' : filtroMarca);
    const filtradas = (analise?.ofs || []).filter(of => {
      // Filtro de status
      if (filtroStatus === 'em_curso' && of.status !== 'em_curso') return false;
      if (filtroStatus === 'terminado' && of.status !== 'terminado' && of.status !== 'arquivado') return false;
      // Filtro de marca (normalizado para casar "One Dilemma" com "one dilema ")
      if (filtroMarcaNorm) {
        const ofMarcaNorm = normMarca(of.marca || of.cliente);
        if (ofMarcaNorm !== filtroMarcaNorm) return false;
      }
      // Filtro de pesquisa
      if (searchText.trim()) {
        const term = searchText.toLowerCase();
        const match = (
          (of.numero_of || '').toLowerCase().includes(term) ||
          (of.cliente || '').toLowerCase().includes(term) ||
          (of.artigo || '').toLowerCase().includes(term) ||
          (of.marca || '').toLowerCase().includes(term)
        );
        if (!match) return false;
      }
      return true;
    });
    // Ordenação configurável
    return filtradas.sort((a, b) => {
      const dA = new Date(a.criado_em || a.data_criacao || 0).getTime();
      const dB = new Date(b.criado_em || b.data_criacao || 0).getTime();
      if (ordenacao === 'antigo') return dA - dB;
      if (ordenacao === 'concluida') {
        const aConc = (a.status === 'terminado' || a.status === 'arquivado') ? 0 : 1;
        const bConc = (b.status === 'terminado' || b.status === 'arquivado') ? 0 : 1;
        if (aConc !== bConc) return aConc - bConc;
        return dB - dA;
      }
      if (ordenacao === 'em_curso') {
        const aCurso = a.status === 'em_curso' ? 0 : 1;
        const bCurso = b.status === 'em_curso' ? 0 : 1;
        if (aCurso !== bCurso) return aCurso - bCurso;
        return dB - dA;
      }
      // default: recente
      return dB - dA;
    });
  }, [analise?.ofs, filtroStatus, filtroMarca, searchText, ordenacao]);

  // Lista de marcas disponíveis (deduplicada via normalização)
  const marcasDisponiveis = useMemo(() => {
    const seen = new Map(); // norm → display name (preserva o primeiro com casing/espacos do user)
    (analise?.ofs || []).forEach(of => {
      const raw = of.marca || of.cliente;
      const norm = normMarca(raw);
      if (!norm) return;
      if (!seen.has(norm)) {
        // Display: trim e capitaliza primeira letra de cada palavra para uniformizar
        const display = String(raw).trim().replace(/\s+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        seen.set(norm, display);
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, 'pt'));
  }, [analise?.ofs]);

  // Paginação (memoizado)
  const totalPages = useMemo(() => Math.max(1, Math.ceil(ofsFiltradas.length / pageSize)), [ofsFiltradas.length, pageSize]);
  const safePage = Math.min(currentPage, totalPages);
  const paginatedOfs = useMemo(() => ofsFiltradas.slice((safePage - 1) * pageSize, safePage * pageSize), [ofsFiltradas, safePage, pageSize]);

  if (loading) {
    return (
      <GestorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </GestorLayout>
    );
  }

  return (
    <GestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900">Análise Financeira</h1>
            <p className="text-stone-600">Custos, tempos e eficiência por OF e operação</p>
          </div>
          <div className="flex gap-3">
            {/* Filtro de Status */}
            <div className="flex bg-stone-100 rounded-lg p-1">
              {[
                { value: 'todos', label: 'Todas' },
                { value: 'em_curso', label: 'Em Curso' },
                { value: 'terminado', label: 'Terminadas' }
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFiltroStatus(f.value)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    filtroStatus === f.value ? 'bg-white shadow-sm text-primary' : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            
            {/* Botões de Exportação */}
            <div className="flex gap-2 items-center">
              {Object.values(selectedOfs).filter(Boolean).length > 0 && (
                <span className="text-xs text-blue-600 font-medium mr-1">
                  {Object.values(selectedOfs).filter(Boolean).length} OF(s)
                </span>
              )}
              <Button variant="outline" size="sm" onClick={exportCSV} data-testid="fin-export-csv">
                <FileSpreadsheet className="h-4 w-4 mr-1" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={exportExcel} data-testid="fin-export-excel">
                <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={exportPDF} data-testid="fin-export-pdf">
                <FileText className="h-4 w-4 mr-1" /> PDF
              </Button>
            </div>
            
            <Button variant="outline" onClick={() => setShowConfigDialog(true)}>
              <Settings className="h-4 w-4 mr-2" /> Configurar
            </Button>
          </div>
        </div>

        {/* Filtros de período e KPIs gerais REMOVIDOS — utilizador prefere ver só OFs ordenadas */}


        {/* Lista de OFs com Detalhe */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b bg-stone-50">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold text-stone-900">Análise por Ordem de Fabrico</h2>
                <p className="text-sm text-stone-500">
                  {ofsFiltradas.length} de {(analise?.ofs || []).length} OF(s)
                  {(filtroMarca !== 'todas' || filtroStatus !== 'todos' || searchText.trim()) && (
                    <button
                      onClick={() => { setFiltroMarca('todas'); setFiltroStatus('todos'); setSearchText(''); }}
                      className="ml-2 text-xs text-blue-600 hover:text-blue-800 underline"
                      data-testid="fin-limpar-filtros"
                    >
                      Limpar filtros
                    </button>
                  )}
                  {Object.values(selectedOfs).filter(Boolean).length > 0 && (
                    <span className="ml-2 text-blue-600 font-medium">
                      ({Object.values(selectedOfs).filter(Boolean).length} selecionada(s) para exportação)
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const allSelected = ofsFiltradas.every(of => selectedOfs[of.of_id]);
                    const next = {};
                    if (!allSelected) {
                      ofsFiltradas.forEach(of => { next[of.of_id] = true; });
                    }
                    setSelectedOfs(next);
                  }}
                  className="flex items-center gap-1.5 text-sm text-stone-600 hover:text-blue-600 transition-colors"
                  data-testid="fin-select-all"
                >
                  {ofsFiltradas.length > 0 && ofsFiltradas.every(of => selectedOfs[of.of_id]) ? (
                    <CheckSquare className="h-4 w-4 text-blue-600" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                  Todas
                </button>
                <span className="text-stone-300">|</span>
                <span className="text-sm text-stone-500">Mostrar:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="h-9 px-2 rounded-md border border-stone-300 text-sm bg-white"
                  data-testid="fin-page-size"
                >
                  {[10, 25, 50, 100].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Pesquisar por OF, cliente, artigo ou marca..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-9"
                data-testid="fin-search-input"
              />
            </div>
            {/* Filtro Marca + Ordenação */}
            <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-stone-200">
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase font-bold text-stone-500">Marca:</span>
                <select
                  value={filtroMarca}
                  onChange={(e) => setFiltroMarca(e.target.value)}
                  className="h-9 px-2 rounded-md border border-stone-300 text-sm bg-white min-w-[140px]"
                  data-testid="fin-filtro-marca"
                >
                  <option value="todas">Todas as marcas ({marcasDisponiveis.length})</option>
                  {marcasDisponiveis.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-3.5 w-3.5 text-stone-400" />
                <span className="text-xs uppercase font-bold text-stone-500">Ordenar:</span>
                <div className="flex bg-stone-100 rounded-lg p-0.5" role="tablist" data-testid="fin-ordenacao">
                  {[
                    { v: 'recente', l: 'Mais recentes' },
                    { v: 'antigo', l: 'Mais antigas' },
                    { v: 'concluida', l: 'Concluídas 1.º' },
                    { v: 'em_curso', l: 'Em curso 1.º' },
                  ].map(o => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setOrdenacao(o.v)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        ordenacao === o.v ? 'bg-white shadow-sm text-stone-800' : 'text-stone-600 hover:text-stone-900'
                      }`}
                      data-testid={`fin-ord-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="divide-y">
            {paginatedOfs.length === 0 ? (
              <div className="p-8 text-center text-stone-500">
                {searchText.trim() ? 'Nenhuma OF corresponde à pesquisa' : 'Nenhuma OF encontrada com os filtros selecionados'}
              </div>
            ) : (
              paginatedOfs.map((of) => (
                <div key={of.of_id}>
                  {/* Linha da OF */}
                  <div 
                    className="p-4 hover:bg-stone-50 cursor-pointer transition-colors"
                    onClick={() => openOfDashboard(of, false)}
                    data-testid={`fin-row-of-${of.numero_of}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button 
                          className="text-stone-400 hover:text-blue-600 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOfs(prev => ({ ...prev, [of.of_id]: !prev[of.of_id] }));
                          }}
                          data-testid={`fin-select-of-${of.numero_of}`}
                        >
                          {selectedOfs[of.of_id] ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                        <button className="text-stone-400" title="Abrir Dashboard Financeiro">
                          <ChevronRight className="h-5 w-5" />
                        </button>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-lg text-navy">{of.numero_of}</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              of.status === 'em_curso' ? 'bg-blue-100 text-blue-700' : 
                              of.status === 'terminado' ? 'bg-emerald-100 text-emerald-700' : 
                              'bg-stone-100 text-stone-600'
                            }`}>
                              {of.status}
                            </span>
                          </div>
                          <p className="text-sm text-stone-500">{of.cliente} • {of.artigo}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6 text-sm">
                        {/* Arranjos/Defeitos com Custos */}
                        <div className="text-center">
                          <p className="text-stone-500 text-xs">Arr./Def.</p>
                          <p className="font-semibold">
                            <span className="text-blue-600">{of.arranjos}</span>
                            /
                            <span className="text-red-600">{of.defeitos}</span>
                          </p>
                          {(of.custo_anomalias > 0 || of.custo_adicional_total > 0) && (
                            <p className="text-xs text-red-500">
                              -{formatCurrency((of.custo_anomalias || 0) + (of.custo_adicional_total || 0))}
                            </p>
                          )}
                        </div>
                        
                        {/* Tempo Previsto vs Real */}
                        <div className="text-center">
                          <p className="text-stone-500 text-xs">Tempo Prev./Real</p>
                          <p className="font-semibold">
                            {formatTempo(of.tempo_previsto_segundos)} / {formatTempo(of.tempo_real_segundos)}
                          </p>
                        </div>
                        
                        {/* Eficiência */}
                        <div className={`text-center px-3 py-1 rounded-lg ${of.tempo_real_segundos > 0 ? getEficienciaBg(of.eficiencia_percentual) : 'bg-stone-50'}`}>
                          <p className="text-stone-500 text-xs">Eficiência</p>
                          <p className={`font-bold text-lg ${of.tempo_real_segundos > 0 ? getEficienciaColor(of.eficiencia_percentual) : 'text-stone-400'}`}>
                            {of.tempo_real_segundos > 0 ? `${of.eficiencia_percentual}%` : '-'}
                          </p>
                        </div>
                        
                        {/* Custo Estimado */}
                        <div className="text-center min-w-[90px]">
                          <p className="text-stone-500 text-xs">Custo Estimado</p>
                          <p className="font-bold text-lg text-stone-500">
                            {formatCurrency((of.tempo_previsto_segundos / 3600) * config.taxa_hora_operador)}
                          </p>
                        </div>
                        
                        {/* Custo Real */}
                        <div className="text-center min-w-[100px]">
                          <p className="text-stone-500 text-xs">Custo Real</p>
                          <p className="font-bold text-lg text-navy">{formatCurrency(of.custo_total)}</p>
                          {of.variacao_custo !== 0 && of.custo_total > 0 && (
                            <p className={`text-xs ${of.variacao_custo > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                              {of.variacao_custo > 0 ? '+' : ''}{formatCurrency(of.variacao_custo)}
                            </p>
                          )}
                          {of.status === 'em_curso' && (
                            <span className="text-[10px] text-blue-500 font-medium">em curso</span>
                          )}
                        </div>
                        
                        {/* Custo/Peça */}
                        <div className="text-center min-w-[80px]">
                          <p className="text-stone-500 text-xs">Custo/Peça</p>
                          <p className="font-bold text-lg text-stone-700">
                            {getQtdFinal(of) && of.custo_total > 0 ? formatCurrency(of.custo_total / getQtdFinal(of)) : (of.custo_total > 0 ? formatCurrency(of.custo_total) : '-')}
                          </p>
                          {of.status === 'em_curso' && of.custo_total > 0 && (
                            <span className="text-[10px] text-blue-500 font-medium">em curso</span>
                          )}
                        </div>

                        {/* Qtd Lote + Qtd Final (read-only — agora última coluna antes dos botões) */}
                        <div className="text-center min-w-[110px] border-l border-stone-200 pl-4">
                          <p className="text-stone-500 text-xs">Qtd Lote</p>
                          <p className="font-semibold text-stone-600">{of.quantidade_total}</p>
                          <p className="text-stone-500 text-xs mt-1">Qtd Final</p>
                          <p className="font-bold text-stone-800">{qtdFinalMap[of.of_id] ?? of.quantidade_final ?? of.quantidade_total}</p>
                        </div>
                        
                        {/* Botão Adicionar Custo / Abrir Dashboard com foco em Custos */}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            openOfDashboard(of, true);
                          }}
                          className="text-stone-500 hover:text-blue-600"
                          title="Adicionar custo extra / Ver dashboard"
                          data-testid={`fin-btn-custo-${of.numero_of}`}
                        >
                          <DollarSign className="h-4 w-4" />
                        </Button>
                        
                        {/* Botão Exportar PDF */}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            exportPDFOf(of);
                          }}
                          title="Exportar PDF desta OF"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                </div>
              ))
            )}
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="p-4 border-t bg-stone-50 flex items-center justify-between">
              <p className="text-sm text-stone-500">
                A mostrar {((safePage - 1) * pageSize) + 1}–{Math.min(safePage * pageSize, ofsFiltradas.length)} de {ofsFiltradas.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage <= 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  data-testid="fin-page-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`dots-${idx}`} className="px-1 text-stone-400">…</span>
                    ) : (
                      <Button
                        key={p}
                        variant={p === safePage ? 'default' : 'outline'}
                        size="sm"
                        className="min-w-[36px]"
                        onClick={() => setCurrentPage(p)}
                        data-testid={`fin-page-${p}`}
                      >
                        {p}
                      </Button>
                    )
                  )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage >= totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  data-testid="fin-page-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Dialog Configurações */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" /> Configurações de Custos
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Taxa Hora Operador */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                  <DollarSign className="h-4 w-4" /> Taxa Hora Operador (€)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={config.taxa_hora_operador}
                  onChange={(e) => setConfig({ ...config, taxa_hora_operador: parseFloat(e.target.value) || 0 })}
                  className="h-12 text-lg"
                />
              </div>
              
              {/* Custos Diários */}
              <div>
                <p className="text-sm font-medium text-stone-700 mb-3">Custos Diários</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">⚡ Energia</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={config.custo_energia_dia || 0}
                      onChange={(e) => setConfig({ ...config, custo_energia_dia: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">💧 Água</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={config.custo_agua_dia || 0}
                      onChange={(e) => setConfig({ ...config, custo_agua_dia: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">📦 Consumíveis</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={config.custo_consumiveis_dia || 0}
                      onChange={(e) => setConfig({ ...config, custo_consumiveis_dia: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              </div>
              
              {/* Percentagens */}
              <div>
                <p className="text-sm font-medium text-stone-700 mb-3">Percentagens</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">% Overhead (%)</label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      value={config.percentagem_overhead || 0}
                      onChange={(e) => setConfig({ ...config, percentagem_overhead: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">📈 Margem Lucro (%)</label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      value={config.margem_lucro || 0}
                      onChange={(e) => setConfig({ ...config, margem_lucro: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveConfig} disabled={saving}>
                {saving ? 'A guardar...' : 'Guardar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Custos Adicionais */}
        <Dialog open={showCustosModal} onOpenChange={setShowCustosModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                Adicionar Custo Extra
              </DialogTitle>
            </DialogHeader>
            
            {selectedOfParaCusto && (
              <div className="space-y-4">
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="font-medium">OF {selectedOfParaCusto.numero_of}</p>
                  <p className="text-sm text-stone-500">{selectedOfParaCusto.cliente} • {selectedOfParaCusto.artigo}</p>
                </div>
                
                <div>
                  <label className="text-sm text-stone-600 mb-1 block">Descrição</label>
                  <Input
                    placeholder="Ex: Material extra, transporte..."
                    value={novoCusto.descricao}
                    onChange={(e) => setNovoCusto({ ...novoCusto, descricao: e.target.value })}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-stone-600 mb-1 block">Valor (€)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={novoCusto.valor}
                      onChange={(e) => setNovoCusto({ ...novoCusto, valor: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm text-stone-600 mb-1 block">Tipo</label>
                    <select
                      className="w-full h-10 px-3 rounded-lg border border-stone-300"
                      value={novoCusto.tipo}
                      onChange={(e) => setNovoCusto({ ...novoCusto, tipo: e.target.value })}
                    >
                      <option value="material">Material</option>
                      <option value="transporte">Transporte</option>
                      <option value="subcontratacao">Subcontratação</option>
                      <option value="outros">Outros</option>
                    </select>
                  </div>
                </div>
                
                {/* Custos existentes */}
                {selectedOfParaCusto.custos_adicionais?.length > 0 && (
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium text-stone-600 mb-2">Custos já registados:</p>
                    <div className="space-y-1">
                      {selectedOfParaCusto.custos_adicionais.map((c, idx) => (
                        <div key={idx} className={`flex items-center justify-between text-sm p-2 rounded ${
                          editingCusto?.id === c.id ? 'bg-blue-50 border border-blue-200' : 'bg-stone-50'
                        }`}>
                          <span className="flex-1">{c.descricao}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-red-600">-{formatCurrency(c.valor)}</span>
                            <button 
                              onClick={() => handleEditCusto(c)}
                              className="text-blue-500 hover:text-blue-700"
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleRemoveCusto(c.id)}
                              className="text-red-500 hover:text-red-700"
                              title="Remover"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => {
                setShowCustosModal(false);
                setNovoCusto({ descricao: '', valor: '', tipo: 'outros' });
                setEditingCusto(null);
              }}>
                Fechar
              </Button>
              {editingCusto ? (
                <>
                  <Button variant="outline" onClick={cancelEditCusto}>
                    Cancelar
                  </Button>
                  <Button onClick={handleUpdateCusto}>
                    Actualizar Custo
                  </Button>
                </>
              ) : (
                <Button onClick={handleAddCusto}>
                  Adicionar Custo
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* === Dashboard Financeiro Detalhado da OF === */}
        <OfFinanceiroDashboard
          open={showOfDashboard}
          onClose={() => setShowOfDashboard(false)}
          of={ofDashboard}
          config={config}
          getQtdFinal={getQtdFinal}
          formatCurrency={formatCurrency}
          formatTempo={formatTempo}
          getDesvioIcon={getDesvioIcon}
          getEficienciaColor={getEficienciaColor}
          CustoUnitarioInput={CustoUnitarioInput}
          onCustosChanged={loadData}
          focoCustos={dashboardFocoCustos}
          qtdFinalAtual={ofDashboard ? (qtdFinalMap[ofDashboard.of_id] ?? ofDashboard.quantidade_final ?? ofDashboard.quantidade_total) : ''}
          onQtdFinalChange={(val) => ofDashboard && setQtdFinalMap(prev => ({ ...prev, [ofDashboard.of_id]: val }))}
          onQtdFinalSave={(val) => ofDashboard && saveQtdFinal(ofDashboard.of_id, val)}
        />
      </div>
    </GestorLayout>
  );
}
